import React, { use, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import SimplePageContent from "../../components/SimplePageContent";
import { FIND_MISSION_TIMESHEETS } from "../../graphql/timesheet";
import { useQuery } from "@apollo/client/react";
import Loading from "../../components/Loading";
import TimesheetList from "../../components/TimeSheetList";
import EmptyListInfo from "../../ui_atoms/EmptyListInfo";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import VStack from "../../ui_atoms/VStack";
import { ProfileType, TimeSheetState } from "../../__generated__/graphql";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { buildErrorHeadband } from "../ScreenCardHeadband";
import ScreenHeader from "./ScreenHeader";

type MissionTimeSheetScreenProps = StackScreenProps<RootStackParamList, "MissionTimeSheets">;

const MissionTimeSheetScreen: React.FC<MissionTimeSheetScreenProps> = ({ route, navigation }) => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const statuses = useMemo(
        () =>
            profileType === ProfileType.Worker
                ? [
                      TimeSheetState.Open,
                      TimeSheetState.Accepted,
                      TimeSheetState.Rejected,
                      TimeSheetState.Submitted,
                  ]
                : [TimeSheetState.Accepted, TimeSheetState.Rejected, TimeSheetState.Submitted],
        [profileType],
    );
    const { data, refetch, loading, error } = useQuery(FIND_MISSION_TIMESHEETS, {
        variables: {
            statusIn: statuses,
            missionId: route?.params.missionId || "",
        },
        fetchPolicy: "cache-and-network",
        skip: !route?.params.missionId,
    });

    const allTimeSheets = data?.findMissionTimeSheets.elements;
    const count = data?.findMissionTimeSheets.count;

    return (
        <>
            <ScreenHeader
                useSmallLabel
                onLeftAction={() => navigation.goBack()}
                label={i18n.t("timesheet_list.title")}
            ></ScreenHeader>
            <SimplePageContent
                refresh={refetch}
                headband={
                    error
                        ? buildErrorHeadband(
                              i18n.t("screens.user.timesheets.cannot_load_timesheets"),
                          )
                        : undefined
                }
            >
                {loading && !allTimeSheets && <Loading />}
                {allTimeSheets && profileType && (
                    <TimesheetList profileType={profileType} timeSheets={allTimeSheets} />
                )}
                {count == 0 && (
                    <VStack h={1} v={1}>
                        <EmptyListInfo text={i18n.t("timesheet_list.empty")} />
                    </VStack>
                )}
            </SimplePageContent>
        </>
    );
};

export default MissionTimeSheetScreen;
